<template>
	<div class="compare-page">

		<section class="compare-hero">
			<div class="compare-content center-content">
				<h1>Compare Systems</h1>
				<h2>Which System Is Right for You?</h2>
				<p>
					Compare Angler™ and Octopus™ to find the underwater monitoring system
					that best fits your project, site, and operational needs.
				</p>
			</div>
		</section>

		<section class="compare-section comparison-matrix">
			<div class="compare-content">
				<div class="pink-line"></div>

				<div class="comparison-table">
					<div class="table-row table-header">
						<div>Feature</div>
						<div>Angler™</div>
						<div>Octopus™</div>
					</div>

					<div class="table-row">
						<div>Camera Type</div>
						<div>Fixed-view underwater camera</div>
						<div>Pan-tilt-zoom underwater camera</div>
					</div>

					<div class="table-row">
						<div>Best For</div>
						<div>Waterfront homes, marinas, yacht clubs, education, conservation, and general observation</div>
						<div>Research institutions, utilities, ports, aquariums, government agencies, and advanced monitoring applications</div>
					</div>

					<div class="table-row">
						<div>Viewing Area</div>
						<div>Continuous monitoring of a specific area</div>
						<div>Remotely explore and inspect large areas</div>
					</div>

					<div class="table-row">
						<div>Camera Control</div>
						<div>Fixed field of view</div>
						<div>Full remote pan, tilt, and zoom control</div>
					</div>

					<div class="table-row">
						<div>Zoom Capability</div>
						<div>Digital zoom</div>
						<div>Optical and digital zoom</div>
					</div>

					<div class="table-row">
						<div>User Experience</div>
						<div>Simple, always-on underwater viewing</div>
						<div>Interactive underwater exploration and inspection</div>
					</div>

					<div class="table-row">
						<div>Live Streaming</div>
						<div>Yes</div>
						<div>Yes</div>
					</div>

					<div class="table-row">
						<div>Remote Viewing</div>
						<div>Yes</div>
						<div>Yes</div>
					</div>

					<div class="table-row">
						<div>Recording & Playback</div>
						<div>Yes</div>
						<div>Yes</div>
					</div>

					<div class="table-row">
						<div>AI Detection Compatible</div>
						<div>Yes</div>
						<div>Yes</div>
					</div>

					<div class="table-row">
						<div>Self-Cleaning Technology</div>
						<div>Yes</div>
						<div>Yes</div>
					</div>

					<div class="table-row">
						<div>Starlink Compatible</div>
						<div>Yes</div>
						<div>Yes</div>
					</div>

					<div class="table-row">
						<div>Sensor Integration</div>
						<div>Available</div>
						<div>Advanced integration capabilities</div>
					</div>

					<div class="table-row">
						<div>Deployment Type</div>
						<div>Permanent or long-term monitoring</div>
						<div>Permanent monitoring and active inspection</div>
					</div>

					<div class="table-row">
						<div>Typical Users</div>
						<div>Homeowners, marinas, educators, conservation groups</div>
						<div>Researchers, utilities, ports, aquariums, government agencies</div>
					</div>

					<div class="table-row">
						<div>Investment Level</div>
						<div>Lower initial investment</div>
						<div>Premium monitoring platform</div>
					</div>

					<div class="table-row">
						<div>Primary Benefit</div>
						<div>Your always-on window into the underwater world</div>
						<div>Maximum visibility, control, and situational awareness</div>
					</div>
				</div>
			</div>
		</section>

 <section class="compare-section technical-specifications">
	<div class="compare-content">
		<div class="pink-line"></div>
		<h2 class="section-title">Technical Specifications</h2>

		<div class="spec-grid">
			<div class="spec-card">
				<h3>Angler™</h3>
				<p class="spec-subtitle">Self-Cleaning Underwater Camera System</p>

				<div class="spec-list">
					<div><strong>Camera Type:</strong> Fixed-view underwater camera</div>
					<div><strong>Video:</strong> 2688 × 1512, 4MP HD, 30 FPS</div>
					<div><strong>Lens:</strong> 2.4 mm, F2.1 wide-angle lens</div>
					<div><strong>Field of View:</strong> 130° horizontal, 93° vertical</div>
					<div><strong>Streaming:</strong> RTSP / H.264</div>
					<div><strong>Self-Cleaning:</strong> Replaceable mechanical brush</div>
					<div><strong>Power:</strong> 90–250 VAC or 12–48 VDC</div>
					<div><strong>Power Consumption:</strong> 5W camera</div>
					<div><strong>Housing:</strong> Acetal and glass</div>
					<div><strong>Depth Rating:</strong> 50 meters</div>
					<div><strong>Cable Length:</strong> &lt;200m standard, &gt;200m Heliax</div>
					<div><strong>Mounting:</strong> Standard or custom mounting available</div>
				</div>
			</div>

			<div class="spec-card">
				<h3>Octopus™</h3>
				<p class="spec-subtitle">Flagship Pan-Tilt-Zoom Vision System</p>

				<div class="spec-list">
					<div><strong>Camera Type:</strong> Pan-tilt-zoom underwater camera</div>
					<div><strong>Video:</strong> 1080p HD, 30 FPS</div>
					<div><strong>Controls:</strong> 360° pan, tilt, optical zoom, focus, autofocus</div>
					<div><strong>Streaming:</strong> RTSP, RTMP, RTMPS, HLS</div>
					<div><strong>Compression:</strong> H.264 and MJPEG</div>
					<div><strong>Audio:</strong> Optional hydrophone</div>
					<div><strong>Self-Cleaning:</strong> CleanSweep™ mechanical wiper</div>
					<div><strong>Power:</strong> 90–250 VAC or 12–48 VDC</div>
					<div><strong>Power Consumption:</strong> 15W max</div>
					<div><strong>Housing:</strong> Acetal and borosilicate glass</div>
					<div><strong>Depth Rating:</strong> 50 meters</div>
					<div><strong>Cable Length:</strong> &lt;70m Ethernet, &gt;2,000m with BelugaLink</div>
					<div><strong>Integration:</strong> Sensors, illuminators, TCP/IP, and serial devices</div>
				</div>
			</div>
		</div>
	</div>
</section>

<section class="compare-section comparison-summary">
	<div class="compare-content">
		<div class="pink-line"></div>
		<h2 class="section-title">Summary</h2>

		<div class="summary-grid">
			<div class="summary-card">
				<h3>Choose Angler™ if you want:</h3>
				<ul>
					<li>A reliable, fixed underwater view</li>
					<li>Continuous observation of marine life or underwater activity</li>
					<li>A cost-effective, low-maintenance solution</li>
					<li>A system for a home, marina, yacht club, school, or conservation project</li>
				</ul>

				<b-button @click="$router.push('/products/Angler')">
					View Angler™
				</b-button>
			</div>

			<div class="summary-card">
				<h3>Choose Octopus™ if you want:</h3>
				<ul>
					<li>The ability to remotely explore underwater environments</li>
					<li>Pan, tilt, and zoom control</li>
					<li>Detailed inspections and research capabilities</li>
					<li>The most advanced underwater monitoring platform available</li>
				</ul>

				<b-button @click="$router.push('/products/octopus')">
					View Octopus™
				</b-button>
			</div>
		</div>

		<div class="button-row">
			<b-button @click="$router.push('/contact')">
				Schedule a Consultation
			</b-button>
		</div>
	</div>
</section>
	</div>
</template>

<script>
export default {
	name: 'CompareSystems'
}
</script>
